/**
 * Pure numbers behind the image crop editor (ISSUE-1094).
 *
 * Every crop area here is in NATURAL pixels of the ROTATED image: the same
 * space react-easy-crop reports as `croppedAreaPixels`. The rotated image is
 * the picked picture turned by `rotation` degrees clockwise, so at 90 or 270
 * its width and height are swapped.
 *
 * No DOM, no canvas and no React in this file, so every rule is unit tested
 * without a browser.
 */

export type typeCropRotation = 0 | 90 | 180 | 270;

export type typeCropArea = {
  x: number;
  y: number;
  width: number;
  height: number;
};

/** An area the cropper reported, with the rotation it was measured at. */
export type typeReportedCropArea = {
  rotation: typeCropRotation;
  area: typeCropArea;
};

export type typeCropOutputType = "image/jpeg" | "image/webp" | "image/png";

/**
 * How the export draws the picture into its canvas. The renderer applies, in
 * this order: `scale(scaleX, scaleY)`, `translate(translateX, translateY)`,
 * `rotate(rotationRadians)`, `drawImage(image, drawX, drawY)`.
 */
export type typeCropDrawPlan = {
  outputWidth: number;
  outputHeight: number;
  scaleX: number;
  scaleY: number;
  translateX: number;
  translateY: number;
  rotationRadians: number;
  drawX: number;
  drawY: number;
};

export const IMAGE_CROP_MIN_ZOOM = 1;
export const IMAGE_CROP_MAX_ZOOM = 3;
export const IMAGE_CROP_ZOOM_STEP = 0.1;
export const IMAGE_CROP_KEYBOARD_STEP_PX = 10;
export const IMAGE_CROP_OUTPUT_QUALITY = 0.92;
export const CROP_OUTPUT_TYPES: readonly typeCropOutputType[] = [
  "image/jpeg",
  "image/webp",
  "image/png",
];

const clamp = (value: number, min: number, max: number): number =>
  Math.min(Math.max(value, min), max);

/** Turns a quarter step clockwise (90) or counterclockwise (-90). */
export function rotateBy(
  rotation: typeCropRotation,
  delta: 90 | -90,
): typeCropRotation {
  return ((((rotation + delta) % 360) + 360) % 360) as typeCropRotation;
}

/** The size of the picture once it is turned by `rotation`. */
export function rotatedSize(
  size: { width: number; height: number },
  rotation: typeCropRotation,
): { width: number; height: number } {
  if (rotation === 90 || rotation === 270) {
    return { width: size.height, height: size.width };
  }
  return { width: size.width, height: size.height };
}

/** The largest centered area of the given aspect inside the rotated picture. */
export function defaultCropArea(input: {
  sourceWidth: number;
  sourceHeight: number;
  rotation: typeCropRotation;
  aspect: number;
}): typeCropArea {
  const bbox = rotatedSize(
    { width: input.sourceWidth, height: input.sourceHeight },
    input.rotation,
  );
  if (bbox.width / bbox.height > input.aspect) {
    const width = Math.round(bbox.height * input.aspect);
    return {
      x: Math.round((bbox.width - width) / 2),
      y: 0,
      width,
      height: bbox.height,
    };
  }
  const height = Math.round(bbox.width / input.aspect);
  return {
    x: 0,
    y: Math.round((bbox.height - height) / 2),
    width: bbox.width,
    height,
  };
}

/**
 * The area to export: the cropper's last report when it belongs to the current
 * rotation and fits the picture, otherwise the centered default. The cropper
 * reports NaN before it has measured its stage, and a stale area after a
 * rotation, so neither may reach the canvas.
 */
export function resolveCropArea(input: {
  reported: typeReportedCropArea | null;
  rotation: typeCropRotation;
  sourceWidth: number;
  sourceHeight: number;
  aspect: number;
}): typeCropArea {
  const bbox = rotatedSize(
    { width: input.sourceWidth, height: input.sourceHeight },
    input.rotation,
  );
  const reported = input.reported;
  if (reported !== null && reported.rotation === input.rotation) {
    const { x, y, width, height } = reported.area;
    const isUsable =
      Number.isFinite(x) &&
      Number.isFinite(y) &&
      Number.isFinite(width) &&
      Number.isFinite(height) &&
      width >= 1 &&
      height >= 1 &&
      x >= 0 &&
      y >= 0 &&
      x + width <= bbox.width + 1 &&
      y + height <= bbox.height + 1;
    if (isUsable) {
      const clampedX = clamp(Math.round(x), 0, bbox.width - 1);
      const clampedY = clamp(Math.round(y), 0, bbox.height - 1);
      return {
        x: clampedX,
        y: clampedY,
        width: clamp(Math.round(width), 1, bbox.width - clampedX),
        height: clamp(Math.round(height), 1, bbox.height - clampedY),
      };
    }
  }
  return defaultCropArea({
    sourceWidth: input.sourceWidth,
    sourceHeight: input.sourceHeight,
    rotation: input.rotation,
    aspect: input.aspect,
  });
}

/**
 * The canvas size and transforms that draw `area` of the rotated picture,
 * never larger than `maxEdgePx` on its longest edge and never upscaled.
 */
export function buildCropDrawPlan(input: {
  sourceWidth: number;
  sourceHeight: number;
  rotation: typeCropRotation;
  area: typeCropArea;
  maxEdgePx: number;
}): typeCropDrawPlan {
  const { area } = input;
  const scale = Math.min(
    1,
    input.maxEdgePx / Math.max(area.width, area.height),
  );
  const outputWidth = Math.max(1, Math.round(area.width * scale));
  const outputHeight = Math.max(1, Math.round(area.height * scale));
  const bbox = rotatedSize(
    { width: input.sourceWidth, height: input.sourceHeight },
    input.rotation,
  );
  return {
    outputWidth,
    outputHeight,
    scaleX: outputWidth / area.width,
    scaleY: outputHeight / area.height,
    translateX: bbox.width / 2 - area.x,
    translateY: bbox.height / 2 - area.y,
    rotationRadians: (input.rotation * Math.PI) / 180,
    drawX: -input.sourceWidth / 2,
    drawY: -input.sourceHeight / 2,
  };
}

/**
 * PNG and WebP may carry transparency, so they are asked for as WebP. Every
 * other pick (JPEG, HEIC, anything) becomes a JPEG on a white background.
 */
export function chooseOutputType(
  sourceType: string,
): "image/jpeg" | "image/webp" {
  return sourceType === "image/png" || sourceType === "image/webp"
    ? "image/webp"
    : "image/jpeg";
}

export function isCropOutputType(type: string): type is typeCropOutputType {
  return (CROP_OUTPUT_TYPES as readonly string[]).includes(type);
}

const OUTPUT_EXTENSIONS: Record<typeCropOutputType, string> = {
  "image/jpeg": "jpg",
  "image/webp": "webp",
  "image/png": "png",
};

/** Keeps the picked file's base name and swaps in the output's extension. */
export function croppedFileName(
  sourceName: string,
  outputType: typeCropOutputType,
): string {
  const dot = sourceName.lastIndexOf(".");
  const base = dot > 0 ? sourceName.slice(0, dot) : sourceName;
  return `${base === "" ? "photo" : base}.${OUTPUT_EXTENSIONS[outputType]}`;
}
